import React, { useCallback, useState } from "react";
import { fetchProviders } from "../misc/fetchProviders";
import { EInfographic } from "./EInfographic";
import _ from "lodash";
import { getBaseValue, roundValue } from "../processors";
import {
    NONRESIDENTIAL_CONNECTION_PROPERTIES_COUNT_KPI_CODE,
    NONRESIDENTIAL_POTABLE_WATER_SUPPLY_KPI_CODE,
    RESIDENTIAL_CONNECTION_PROPERTIES_COUNT_KPI_CODE,
    RESIDENTIAL_POTABLE_WATER_SUPPLY_KPI_CODE,
    SEWERAGE_CAPEX_KPI_CODE,
    SEWERAGE_LENGTH_OF_MAINS_KPI_CODE,
    SEWERAGE_OPERATING_COST_KPI_CODE,
    SEWERAGE_OTHER_COST_KPI_CODE,
    SEWERAGE_WATER_TREATMENT_COUNT,
    WaterProvider,
    WATER_SERVICE_PROVIDER,
    WATER_SUPPLY_CAPEX_KPI_CODE,
    WATER_SUPPLY_LENGTH_OF_MAINS_KPI_CODE,
    WATER_SUPPLY_OPERATING_COST_KPI_CODE,
    WATER_SUPPLY_OTHER_COST_KPI_CODE,
    WATER_SUPPLY_WATER_TREATMENT_COUNT
} from "../misc";
import { queryKpisOpenData } from "../misc/kpiOpenApi";
import { useAsync } from "../hooks";

const metricLoader: Record<
    | "waterMainsLength"
    | "sewerageMainsLength"
    | "waterTreatmentPlantsCount"
    | "sewerageTreatmentPlantsCount"
    | "connectedPropertiesCount"
    | "potableWaterSuppliedCapacity"
    | "capitalOperationsSpentAmount",
    {
        kpis: string[][];
    }
> = {
    waterMainsLength: {
        kpis: [[WATER_SUPPLY_LENGTH_OF_MAINS_KPI_CODE, WATER_SERVICE_PROVIDER]]
    },
    sewerageMainsLength: {
        kpis: [[SEWERAGE_LENGTH_OF_MAINS_KPI_CODE, WATER_SERVICE_PROVIDER]]
    },
    waterTreatmentPlantsCount: {
        kpis: [[WATER_SUPPLY_WATER_TREATMENT_COUNT, WATER_SERVICE_PROVIDER]]
    },
    sewerageTreatmentPlantsCount: {
        kpis: [[SEWERAGE_WATER_TREATMENT_COUNT, WATER_SERVICE_PROVIDER]]
    },
    connectedPropertiesCount: {
        kpis: [
            [RESIDENTIAL_CONNECTION_PROPERTIES_COUNT_KPI_CODE, WATER_SERVICE_PROVIDER],
            [NONRESIDENTIAL_CONNECTION_PROPERTIES_COUNT_KPI_CODE, WATER_SERVICE_PROVIDER]
        ]
    },
    potableWaterSuppliedCapacity: {
        kpis: [
            [RESIDENTIAL_POTABLE_WATER_SUPPLY_KPI_CODE, WATER_SERVICE_PROVIDER],
            [NONRESIDENTIAL_POTABLE_WATER_SUPPLY_KPI_CODE, WATER_SERVICE_PROVIDER]
        ]
    },
    capitalOperationsSpentAmount: {
        kpis: [
            [WATER_SUPPLY_OPERATING_COST_KPI_CODE, WATER_SERVICE_PROVIDER],
            [SEWERAGE_OPERATING_COST_KPI_CODE, WATER_SERVICE_PROVIDER],
            [WATER_SUPPLY_CAPEX_KPI_CODE, WATER_SERVICE_PROVIDER],
            [SEWERAGE_CAPEX_KPI_CODE, WATER_SERVICE_PROVIDER]
            // [WATER_SUPPLY_OTHER_COST_KPI_CODE, WATER_SERVICE_PROVIDER],
            // [SEWERAGE_OTHER_COST_KPI_CODE, WATER_SERVICE_PROVIDER]
        ]
    }
};

interface Measure {
    waterServiceProviderCount: number;
    capitalOperationsSpentAmount: number;
    waterMainsLength: number;
    sewerageTreatmentPlantsCount: number;
    waterTreatmentPlantsCount: number;
    sewerageMainsLength: number;
    potableWaterSuppliedCapacity: number;
    connectedPropertiesCount: number;
}

const useEInfographicWithNumbers = year => {
    const [measures, setMeasures] = useState<Measure>({
        waterServiceProviderCount: null,
        capitalOperationsSpentAmount: null,
        waterMainsLength: null,
        sewerageTreatmentPlantsCount: null,
        waterTreatmentPlantsCount: null,
        sewerageMainsLength: null,
        potableWaterSuppliedCapacity: null,
        connectedPropertiesCount: null
    });

    const loadData: () => Promise<Measure> = useCallback(
        async (signal?: AbortSignal) => {
            if (year == null) return null;

            let providers: WaterProvider[] = [];

            try {
                providers = await fetchProviders({
                    signal
                });
            } catch (err) {
                console.log(err);
            }

            // const excess = providers?.filter(
            //     d => d.residentialTariffStruct === "fixedEntitlementAndExcessConsumptionCharge"
            // );
            // console.log(providers, excess);

            let nameToProviders = _.keyBy(providers, d => d.kapiName);

            const values = await Promise.all(
                Object.entries(metricLoader).map(params => {
                    const key = params[0];
                    const { kpis } = params[1];

                    return queryKpisOpenData({ kpis, years: [year] }, [])
                        .then(records => {
                            records = records.filter(
                                d =>
                                    (key === "capitalOperationsSpentAmount" &&
                                        nameToProviders?.[d.organisation] != null &&
                                        nameToProviders?.[d.organisation]?.connectionSizeCategory !== "bulk") ||
                                    (key !== "capitalOperationsSpentAmount" &&
                                        nameToProviders?.[d.organisation] != null)
                            );
                            // const latestYear = _.max(records?.map(d => d.year));
                            const unit = getBaseValue(0, records?.find(d => d.unit != null)?.unit)?.unit;

                            // console.log(latestYear);

                            if (year == null || unit == null)
                                return {
                                    key,
                                    value: null
                                };

                            const lastYearRecords = records.filter(d => d.year === year).filter(d => d.value != null);
                            const lastYearBaseRecords = lastYearRecords.map(d => getBaseValue(d?.value, d?.unit));
                            const lastYearBaseUnit = lastYearBaseRecords?.[0]?.unit;
                            const beforeRound = _.sumBy(lastYearBaseRecords, d => d.value);

                            // transform kL to ML regardless of values
                            let value = roundValue(beforeRound, lastYearBaseUnit, true)?.value;

                            if (key === "capitalOperationsSpentAmount") value = value / 1000000000;

                            const mulTh = _.floor(value / 1000);
                            const rem = value % 1000;

                            // console.log([mulTh, rem]);
                            const finalValue = mulTh === 0 ? rem : mulTh * 1000;

                            return {
                                key,
                                value: finalValue
                            };
                        })
                        .catch(err => {
                            console.log(err);
                            return {
                                key,
                                value: null
                            };
                        });
                })
            );

            const newMeasures = {
                waterServiceProviderCount: providers.length,
                capitalOperationsSpentAmount: null,
                waterMainsLength: null,
                sewerageTreatmentPlantsCount: null,
                waterTreatmentPlantsCount: null,
                sewerageMainsLength: null,
                potableWaterSuppliedCapacity: null,
                connectedPropertiesCount: null
            };

            const metricValues = values.reduce((prev, current) => {
                prev[current.key] = current.value;
                return prev;
            }, {});

            return {
                ...newMeasures,
                ...metricValues
            };
        },
        [year]
    );

    const onLoadData = useCallback((measures: Measure) => {
        if (measures == null) return;

        setMeasures(measures);
    }, []);

    useAsync(loadData, onLoadData);

    return measures;
};

export const EInfographicWithNumbers: React.FC<{
    className?: string;
    year?: number;
}> = ({ className = "", year }) => {
    const {
        waterServiceProviderCount,
        capitalOperationsSpentAmount,
        waterMainsLength,
        sewerageMainsLength,
        connectedPropertiesCount,
        potableWaterSuppliedCapacity,
        sewerageTreatmentPlantsCount,
        waterTreatmentPlantsCount
    } = useEInfographicWithNumbers(year);

    return (
        <EInfographic
            className={className}
            waterServiceProviderCount={waterServiceProviderCount}
            capitalOperationsSpentAmount={capitalOperationsSpentAmount}
            waterMainsLength={waterMainsLength}
            sewerageMainsLength={sewerageMainsLength}
            connectedPropertiesCount={connectedPropertiesCount}
            potableWaterSuppliedCapacity={potableWaterSuppliedCapacity}
            sewerageTreatmentPlantsCount={sewerageTreatmentPlantsCount}
            waterTreatmentPlantsCount={waterTreatmentPlantsCount}
        />
    );
};
